import { I18nManager, NativeModules, Platform } from 'react-native';
import type { AppLocale } from './messages';

/**
 * Язык устройства на первом запуске (аудит удобства 2026-10-04, п. 32).
 *
 * <p><b>Что было.</b> Без сохранённого выбора приложение всегда открывалось по-английски:
 * русскоязычный человек видел английский экран входа и не находил на нём переключателя.
 *
 * <p><b>Почему без expo-localization.</b> Он — нативный модуль, то есть новая сборка EAS ради
 * одной строки. Всё нужное уже есть в рантайме: `Intl` (Hermes собран с ним), в вебе
 * `navigator.languages`, а на телефоне — константы `I18nManager` (Android) и `SettingsManager`
 * (iOS) из самого React Native. Источников несколько, потому что каждый где-то пуст: Hermes
 * без полной ICU отдаёт `en-US` вместо настоящего языка, `SettingsManager` есть только на iOS.
 */

/**
 * Какой из наших языков ближе всего к списку предпочтений человека. Берём первый, который мы
 * знаем: второй язык в списке — тоже выбор человека, и он лучше английского по умолчанию.
 *
 * <p>Близкие языки сводятся к своим: хорватский, боснийский и черногорский читаются как
 * сербская латиница, белорусский и казахский интерфейс у нас ближе всего к русскому.
 * Украинский к русскому намеренно не сводится — это был бы выбор за человека, и не тот.
 */
export function pickAppLocale(tags: readonly (string | null | undefined)[]): AppLocale | null {
  for (const raw of tags) {
    if (typeof raw !== 'string') continue;
    const lang = raw.trim().replace('_', '-').split('-')[0]?.toLowerCase() ?? '';
    if (lang === 'ru' || lang === 'be' || lang === 'kk' || lang === 'ky') return 'ru';
    if (lang === 'sr' || lang === 'sh' || lang === 'hr' || lang === 'bs' || lang === 'cnr') return 'sr';
    if (lang === 'fr') return 'fr';
    if (lang === 'en') return 'en';
  }
  return null;
}

/** Все языковые метки, которые удаётся достать на этой платформе, — по убыванию доверия. */
function deviceLanguageTags(): string[] {
  const tags: string[] = [];
  const push = (v: unknown) => {
    if (typeof v === 'string' && v.trim().length > 0) tags.push(v);
  };
  try {
    if (Platform.OS === 'web') {
      const nav = (globalThis as { navigator?: { languages?: readonly string[]; language?: string } })
        .navigator;
      nav?.languages?.forEach(push);
      push(nav?.language);
    } else if (Platform.OS === 'ios') {
      const settings = (NativeModules.SettingsManager as
        | { settings?: { AppleLanguages?: string[]; AppleLocale?: string } }
        | undefined)?.settings;
      settings?.AppleLanguages?.forEach(push);
      push(settings?.AppleLocale);
    } else {
      const constants = (I18nManager as unknown as {
        getConstants?: () => { localeIdentifier?: string };
      }).getConstants?.();
      push(constants?.localeIdentifier);
    }
  } catch {
    // Модуля нет (тест, ранний старт) — ниже остаётся Intl.
  }
  try {
    push(Intl.DateTimeFormat().resolvedOptions().locale);
  } catch {
    /* Intl недоступен — значит, языка не узнать */
  }
  return tags;
}

/** Язык для человека, который ещё ничего не выбирал; `null` — ни один из наших не подошёл. */
export function detectDeviceLocale(): AppLocale | null {
  return pickAppLocale(deviceLanguageTags());
}
